import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { ThemeProvider as MuiThemeProvider } from "@mui/material";
import { useLocation } from "react-router-dom";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppState } from "./hooks/useOptimizedState";
import { useWebSocket, webSocketService } from "./hooks/useWebSocket";
import { tokenRefreshManager } from "./utils/tokenRefresh";
import { getAuthTokens } from "./utils/cookieUtils.js";
import "./Style/Fonts.css";
import "./Style/lightModeEnhancements.css";
import { LicenseInfo } from '@mui/x-license';
import {
  setMarketData,
  setMarkets,
} from "./Redux/Reducers/marketDataSlice";

// Import accounting slice actions
import {
  setAccountingData,
  updateAccountingAum,
  addAccountingAum,
} from "./Redux/Reducers/accountingSlice";

// Import tickers slice actions
import {
  setTickers,
} from "./Redux/Reducers/tickersSlice";

// Import routes slice actions
import {
  setRoutes,
} from "./Redux/Reducers/routeSlice";

// Import FTP slice actions
import {
  setFtp,
} from "./Redux/Reducers/ftpSlice";

// Import UI slice actions
import {
  updateLoading,
  updateLoadingMsg,
  updateIsInvestor,
  updateIsStrategist,
  removeActiveRequests,
  addActiveRequests,
  markDateAsFetched,
  setWebsocketDisconnected,
  setWebsocketReconnect,
  setReconnecting,
} from "./Redux/Reducers/uiSlice";

// Import new funds slice actions
import {
  setFunds,
  setFundsWithLotsAndStrategies,
  addFund,
  addSMA,
  updateSMA,
  updateFund,
  deleteFund as deleteFundNew,
  updateFundAllocation,
  blacklistRulesFunds,
  blacklistRulesModifiedFunds,
  blacklistDeleteFunds,
} from "./Redux/Reducers/fundsSlice";


// Import strategies slice actions
import {
  setStrategies,
  addStrategy,
  updateStrategy,
  deleteStrategy,
  setHistoricalSignalSet,
  updateStrategySignal,
  setBlacklistSignal,
  setNewsData,
} from "./Redux/Reducers/strategiesSlice";

// Import reports slice actions
import {
  setReports,
  setFile,
  updateReport,
  addReport,
  setTradeCustodianAccountingFile,
} from "./Redux/Reducers/reportsSlice";

// Import lots slice actions
import {
  setLots
} from "./Redux/Reducers/lotsSlice";

// Import trades slice actions
import {
  setTrades,
  addTrades,
  addOrders,
  addManuals,
  addManual,
  addFill,
  addCommission,
  deleteTrades,
  setDeletes,
  setHistoricalTrades,
  setProcessedTrades,
} from "./Redux/Reducers/tradesSlice";

// Import rebalancer slice actions
import {
  setAllTrades,
  setSleeveDataStageOne,
  setValidatedData,
  setFinalCrossedTrades,
  setRebalancerOrders,
  setSwapStrategies,
  setSwapRebalanceStrategies,
  setSwapRebalanceStrategiesValidity,
  setSwapFinalStrategies,
  rebalancerLocatesRedo,
  setRebalancerSummary,
  setBlacklistedTickers,
  updateSwapLoading,
} from "./Redux/Reducers/rebalancerSlice";

// Import custodian slice actions
import {
  setCustodians,
  addCustodian,
  updateCustodian,
  deleteCustodian as deleteCustodianNew,
} from "./Redux/Reducers/custodianSlice";

// Import broker slice actions
import {
  setBrokers,
  addBroker,
  updateBroker,
  deleteBroker as deleteBrokerNew,
} from "./Redux/Reducers/brokerSlice";

// Import securities slice actions
import {
  setSecurities,
  updateSecurities,
  updateFigi,
} from "./Redux/Reducers/securitiesSlice";

// Import notifications slice actions
import {
  setNotifications,
  updateNotification,
  updateUnreadNotification,
} from "./Redux/Reducers/notificationsSlice";

// Import managers slice actions
import {
  setManagers,
  updateManagerSMAs,
  blacklistRulesManager,
  blacklistRulesModifiedManager,
  blacklistDeleteManagers,
} from "./Redux/Reducers/managersSlice";





import {
  getUser,
  getUserInfo,
  removeUser,
  getApiInfo,
} from "./Redux/Actions/UserActions";

import { ToastContainer, toast } from "react-toastify";
import MainRoutes from "./MainRoutes";
import Sockets from "./utils/s3Utils";
import { processFundsWithLotsAndStrategies } from "./component/Methods";

import { StackedBottomRightLoaders } from "./component/spinner/BottomRightLoader";
import { InitialLoadSkeleton } from "./component/spinner/InitialLoadSkeleton";
import { WebsocketDisconnectionNotification } from "./component/spinner/WebsocketDisconnectionNotification";
import { storeSeedData, } from "./utils/seedManager";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY);




// Remove the duplicate custom hooks since they're now in the separate file
function App() {
  // Use optimized custom hooks instead of multiple useSelector calls
  const {
    user,
    managers,
    socketapiData,
    loadingmsg,
    loading,
    req,
    showMarketLoader,
    websocketDisconnected,
    websocketReconnectMode
  } = useAppState();

  // Use WebSocket service instead of global window.clientSocks
  const { send, connect, disconnect, reconnect, markSeedDataReceived, resetSeedDataFlag } = useWebSocket();

  // Use JWT token from user state instead of session token
  const token = user?.idToken;
  const [showHideButton, setShowHideButton] = useState(false);
  const [showInitialSkeleton, setShowInitialSkeleton] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const apiDataRef = useRef(socketapiData);
  const timerRef = useRef(null);

  useEffect(() => {
    apiDataRef.current = socketapiData;
  }, [socketapiData]);

  // Ensure showInitialSkeleton is false when on login page without a user
  useEffect(() => {
    if ((location.pathname === "/login" || location.pathname === "/") && !user) {
      setShowInitialSkeleton(false);
    }
  }, [location.pathname, user]);

  useEffect(() => {
    // Check for stored user (from cookies and localStorage)
    let localUser = getAuthTokens();
    if (localUser && localUser.username) {
      let userAuth = localUser;

      // Migration logic: Detect old username/password format and clear it
      if (userAuth.username && userAuth.password && !userAuth.idToken) {
        localStorage.removeItem("user");
        dispatch(updateLoading(false));
        return;
      }

      //check if username is not an email.
      if (!userAuth.username || typeof userAuth.username !== 'string' || !userAuth.username.includes("@")) {
        localStorage.removeItem("user");
        dispatch(updateLoading(false));
        return;
      }

      // Only proceed if we have the new token-based format
      if (!userAuth.idToken || !userAuth.accessToken) {
        console.warn("Invalid user data format, clearing and requiring re-login");
        localStorage.removeItem("user");
        dispatch(updateLoading(false));
        return;
      }

      // Proceed with stored tokens - auth errors will be handled by WebSocket reconnection
      dispatch(updateLoadingMsg("Authenticating"));
      setShowInitialSkeleton(true);
      dispatch(getUser(userAuth));
    }
  }, []);

  // Timer effect for showing hide button after 5 minutes
  useEffect(() => {
    if (loading) {
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Start new 5-minute timer
      timerRef.current = setTimeout(() => {
        setShowHideButton(true);
      }, 5 * 60 * 1000); // 5 minutes
    } else {
      // Reset when loading becomes false
      setShowHideButton(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [loading]);

  // Sync localStorage user changes to Redux state
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user") {
        if (e.newValue) {
          // User data was updated
          try {
            const updatedUser = JSON.parse(e.newValue);
            // Only dispatch if we have a valid user object with tokens
            if (updatedUser && updatedUser.idToken && updatedUser.accessToken) {
              console.log("[STORAGE SYNC] Syncing user update to Redux");
              dispatch(getUser(updatedUser));
            }
          } catch (error) {
            console.warn("[STORAGE SYNC] Failed to parse user from localStorage:", error);
          }
        } else {
          // User data was cleared (logout)
          console.log("[STORAGE SYNC] User cleared from localStorage");
          dispatch(removeUser());
        }
      }
    };

    // Listen for localStorage changes (works across tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  let connected_user = "strategist";

  const messageHandlers = {

    seed: (data) => {
      if (!data?.data) return;

      // Store seed data for error debugging
      storeSeedData(data.data, {
        source: 'websocket-seed-message',
        receivedAt: new Date().toISOString(),
        userId: user?.id,
        messageType: 'seed'
      });



      dispatch(setBrokers(data.data.brokers));
      dispatch(setManagers(data.data.managers));
      dispatch(setMarkets(data.data.markets));
      dispatch(setRoutes(data.data.routers));


      const userType = determineUserType(data.data.managers, user);
      connected_user = userType;
      if (userType === "admin" || userType === "investor") {
        handleAdminInvestorData(data.data);
      } else {
        handleStrategistData(data.data);
      }


      dispatch(setCustodians(data.data.custodians));
      dispatch(setTickers(data.data.tickers));
      dispatch(setTrades(data.data.trades));
      dispatch(setDeletes(data.data.trades));
      dispatch(setNotifications(data.data.notifications));

      dispatch(removeActiveRequests("seed"));
      setShowInitialSkeleton(false);

      // Mark that seed data has been received - enables reconnection attempts
      markSeedDataReceived();

      // Hide reconnection notification after seed data is fully processed
      dispatch(setWebsocketReconnect(false));

      const isReconnection = hasCompletedInitialSeedRef.current;
      if (!isReconnection) {
        const currentPath = location.pathname;
        if (currentPath === "/login" || currentPath === "/" || currentPath === "/signup" || currentPath === "/forgot") {
          // Navigate to appropriate dashboard based on user type
          if (data.data.managers && data.data.managers.length > 0) {
            navigate("/vantage", { replace: true });
          } else {
            navigate("/portfolio", { replace: true });
          }
        }
      }

    },


    update_accounting_aum: (data) => {
      dispatch(updateAccountingAum(data?.data));
      dispatch(removeActiveRequests("update_accounting_aum"));
    },
    add_accounting_aum: (data) => {
      dispatch(addAccountingAum(data?.data));
      dispatch(removeActiveRequests("update_accounting_aum"));
    },

    new_broker: (data) => {
      dispatch(removeActiveRequests("new_broker"));
      return dispatch(addBroker(data?.data?.broker));
    },

    modify_broker: (data) => {
      dispatch(updateBroker(data?.data?.broker));
      dispatch(removeActiveRequests("modify_broker"));

    },

    delete_broker: (data) => {
      dispatch(deleteBrokerNew(data?.data?.broker));
      dispatch(removeActiveRequests("delete_broker"));
    },

    new_custodian: (data) => {
      dispatch(removeActiveRequests("new_custodian"));
      return dispatch(addCustodian(data?.data?.custodian));
    },

    modify_custodian: (data) => {
      dispatch(removeActiveRequests("modify_custodian"));
      dispatch(updateCustodian(data?.data?.custodian));
    },

    delete_custodian: (data) => {
      dispatch(deleteCustodianNew(data?.data?.custodian));
      dispatch(removeActiveRequests("delete_custodian"));
    },

    create_fund: (data) => {
      dispatch(removeActiveRequests("add_fund"));
      dispatch(addFund(data?.data)); // Use new funds slice
    },

    modify_fund: (data) => {
      dispatch(removeActiveRequests("modify_fund"));
      dispatch(updateFund(data?.data?.fund)); // Use new funds slice
    },

    delete_fund: (data) => {
      dispatch(deleteFundNew(data?.data?.fund)); // Use new funds slice
      dispatch(removeActiveRequests("delete_fund"));
    },

    new_sma: (data) => {
      dispatch(removeActiveRequests("new_sma"));
      dispatch(addSMA(data?.data)); // Use new funds slice
      dispatch(updateManagerSMAs(data?.data)); // Also add to managers[0].smas
    },

    update_sma: (data) => {
      dispatch(removeActiveRequests("update_sma"));
      dispatch(updateSMA(data?.data)); // Use new funds slice
      dispatch(updateManagerSMAs(data?.data)); // Also update in managers[0].smas
    },

    market_data: (data) => {
      dispatch(setMarketData(data?.data));
      dispatch(removeActiveRequests("market_data"));
    },



    get_historical_signal_set: (data) => {

      dispatch(setHistoricalSignalSet(data?.data));
      dispatch(removeActiveRequests("get_historical_signal_set"));
    },

    get_archived_trades: (data) => {
      if (!data?.data) return;
      dispatch(setHistoricalTrades(data.data));
      dispatch(removeActiveRequests("get_archived_trades"));
      if (data.data.date) {
        dispatch(markDateAsFetched(data.data.date));
      }
    },

    new_strategy: (data) => {
      dispatch(removeActiveRequests("new_strategy"));
      return dispatch(addStrategy(data?.data));
    },

    update_strategy: (data) => {
      dispatch(updateStrategy(data?.data));
      dispatch(removeActiveRequests("modify_strategy"));
    },

    get_security_master: (data) => {
      if (!data?.data) return;
      dispatch(setSecurities(data.data));
      dispatch(removeActiveRequests("get_security_master"));
    },

    modify_security_master_items: (data) => {
      if (!data?.data) return;
      dispatch(updateSecurities(data.data));
      dispatch(removeActiveRequests("modify_security_master_items"));
    },

    delete_strategy: (data) => {
      dispatch(deleteStrategy(data?.data));
      dispatch(removeActiveRequests("delete_strategy"));
    },

    get_accounting_eod: (data) => {
      dispatch(setTradeCustodianAccountingFile(data?.data));
    },

    new_trades: (data) => {
      if (!data?.data) return;
      if (connected_user !== "strategist") {
        toast.success("New trades are added.");
      }
      dispatch(addTrades(data.data));
      dispatch(setLots({ lots: [], trades: [] }));
      dispatch(removeActiveRequests("new_order"));
    },

    modify_orders: (data) => {
      if (!data?.data) return;
      if (connected_user !== "strategist") {
        toast.success("Trade is Updated.");
      }
      dispatch(removeActiveRequests("modify_orders"));
      dispatch(addOrders(data));
      dispatch(setLots({ lots: [], trades: [] }));
    },

    modify_trade_fills: (data) => {
      if (!data?.data) return;
      dispatch(addManual(data.data));
      dispatch(removeActiveRequests("modify_trade_fills"));
      dispatch(setLots({ lots: [], trades: [] }));
    },

    modify_trade_commissions: (data) => {
      if (!data?.data) return;
      dispatch(removeActiveRequests("modify_trade_commissions"));
      dispatch(addCommission(data));
    },

    add_bulk_fills: (data) => {
      if (!data?.data) return;
      if (connected_user !== "strategist") {
        toast.success("Fills are added.");
      }

      dispatch(addManuals(data.data));
    },

    update_fills: (data) => {
      if (!data?.data?.fill) return;
      dispatch(addFill(data.data));
      dispatch(setLots({ lots: [], trades: [] }));
    },

    route_orders: () => {
      if (connected_user !== "strategist") {
        toast.success("Orders routed successfully.");
      }
    },

    // Rebalancer handlers
    rebalance_swaps_step1: (data) => {

      dispatch(setSwapStrategies(data?.data));
      dispatch(removeActiveRequests("rebalance_swaps"));
      dispatch(updateSwapLoading(false));
    },

    rebalance_swaps_step2: (data) => {
      dispatch(setSwapRebalanceStrategies(data?.data));
      dispatch(removeActiveRequests("rebalance_swaps"));
    },

    rebalance_swaps_step3: (data) => {
      dispatch(setSwapRebalanceStrategiesValidity(data?.data));
      dispatch(removeActiveRequests("rebalance_swaps"));
    },

    rebalance_swaps_step4: (data) => {
      dispatch(setSwapRebalanceStrategiesValidity(data?.data));
      dispatch(removeActiveRequests("rebalance_swaps"));
    },

    rebalance_swaps_step5: (data) => {
      dispatch(setSwapFinalStrategies(data?.data));
      dispatch(removeActiveRequests("rebalance_swaps"));
    },

    get_rebalancer_crossing_orders: (data) => {
      if (!data?.data?.data) return;
      dispatch(setProcessedTrades(data.data.data));


    },

    generate_file: (data) => {
      return dispatch(setFile(data?.data));
    },

    create_report: (data) => {
      dispatch(removeActiveRequests("create_report"));
      return dispatch(addReport(data?.data));
    },

    generate_report: (data) => {
      dispatch(removeActiveRequests("generate_report"));
      return dispatch(updateReport(data?.data));
    },

    archive_trades: (data) => {
      if (!data?.data) return;
      if (connected_user !== "strategist") {
        toast.success("Trades Archived Successfully.");
      }
      dispatch(removeActiveRequests("archive_trades"));
      return dispatch(deleteTrades(data));
    },

    delete_trades: (data) => {
      if (!data?.data) return;
      if (connected_user !== "strategist") {
        toast.success("Trades Deleted Successfully.");
      }
      dispatch(removeActiveRequests("delete_trades"));
      dispatch(deleteTrades(data));
    },

    post_ftp: (data) => {
      return dispatch(setFtp(data?.data));
    },

    manager_sync: () => {
      toast.success("Manager Synced");
      dispatch(removeActiveRequests("sync_manager"));
    },

    new_signal: (data) => {
      if (!data) return;
      toast.success("Strategy Signal Updated.");
      dispatch(removeActiveRequests("new_signal"));
      dispatch(updateStrategySignal(data));
    },

    get_news: (data) => {
      if (!data?.data) return;
      dispatch(removeActiveRequests("get_news"));
      dispatch(setNewsData(data.data));

    },

    blacklisted_tickers: (data) => {
      if (!data?.data) return;
      dispatch(setBlacklistSignal(data.data));
      toast.error("Signal list could not be updated because blacklisted tickers are found.");
    },

    new_notification: (data) => {
      if (!data) return;
      dispatch(updateNotification(data));
    },

    modify_notifications: (data) => {
      if (!data) return;
      dispatch(updateUnreadNotification(data));
    },


    rebalancer_check_blacklist: (data) => {
      if (!data) return;
      dispatch(setBlacklistedTickers(data?.data));
      dispatch(removeActiveRequests("rebalancer_check_blacklist"));
    },


    create_rebalancer_items: (data) => {
      if (!data) return;
      dispatch(removeActiveRequests("create_rebalancer_items"));
      dispatch(setSleeveDataStageOne(data?.data));
    },

    create_rebalancer_orders: (data) => {
      if (!data?.data) return;
      dispatch(setRebalancerOrders(data.data.orders));
      dispatch(removeActiveRequests("create_rebalancer_orders"));
    },

    create_rebalancer_crossing_orders: (data) => {
      dispatch(setAllTrades(data?.data?.data));
      dispatch(removeActiveRequests("create_rebalancer_crossing_orders"));
    },
    create_fund_rebalancer_trades: (data) => {
      dispatch(setFinalCrossedTrades(data?.data?.data));
      dispatch(removeActiveRequests("create_fund_rebalancer_trades"));
    },
    rebalancer_validate: (data) => {
      dispatch(setValidatedData(data?.data?.data));
      dispatch(removeActiveRequests("rebalancer_validate"));
    },

    rebalancer_locates: (data) => {
      dispatch(setFinalCrossedTrades(data?.data?.data));
      dispatch(removeActiveRequests("rebalancer_locates"));
    },

    rebalancer_locates_redo: (data) => {
      dispatch(rebalancerLocatesRedo(data?.data?.data?.orders));
      dispatch(removeActiveRequests("rebalancer_locates"));
    },

    rebalancer_summary: (data) => {
      dispatch(setRebalancerSummary(data?.data));
      dispatch(removeActiveRequests("rebalancer_summary"));
    },

    rebalancer_complete: () => {

      dispatch(removeActiveRequests("finalize_rebalancer_orders"));
      if (connected_user !== "strategist") {
        toast.success("Rebalancer Orders Created");
        navigate("/tradesV2");
      }

    },

    fund_allocation: (data) => {
      if (!data) return;
      dispatch(removeActiveRequests("set_fund_allocation"));
      dispatch(updateFundAllocation(data?.data)); // Use new funds slice
    },

    fetch_figi: (data) => {
      if (!data) return;
      dispatch(updateFigi(data?.data));
      dispatch(removeActiveRequests("fetch_figi"));
    },

    create_fund_blacklist_rule: (data) => {
      if (!data?.data) return;
      dispatch(blacklistRulesFunds(data.data));
      if (connected_user !== "strategist") {
        toast.success("Blacklisted Rules Created.");
      }
      dispatch(removeActiveRequests("create_blacklist_rule"));
    },

    modify_fund_blacklist: (data) => {
      if (!data) return;
      dispatch(blacklistRulesModifiedFunds(data?.data));
      if (connected_user !== "strategist") {
        toast.success("Blacklisted Rules Updated.");
      }
      dispatch(removeActiveRequests("modify_blacklist"));
    },

    delete_fund_blacklist: (data) => {
      if (!data?.data) return;
      dispatch(blacklistDeleteFunds(data.data));
      toast.success("Blacklist is Deleted.");
      dispatch(removeActiveRequests("delete_blacklist"));
    },

    create_manager_blacklist_rule: (data) => {
      if (!data?.data) return;
      dispatch(blacklistRulesManager(data.data));
      toast.success("Blacklisted Rules Created.");
      dispatch(removeActiveRequests("create_blacklist_rule"));
    },

    modify_manager_blacklist: (data) => {
      if (!data) return;
      dispatch(blacklistRulesModifiedManager(data?.data));
      toast.success("Blacklisted Rules Updated.");
      dispatch(removeActiveRequests("modify_blacklist"));
    },

    delete_manager_blacklist: (data) => {
      if (!data?.data) return;
      dispatch(blacklistDeleteManagers(data.data));
      toast.success("Blacklist is Deleted.");
      dispatch(removeActiveRequests("delete_blacklist"));
    },

    create_rebalancer_crossing_orders_error: (data) => {
      if (!data?.data) return;
      dispatch(removeActiveRequests("create_rebalancer_crossing_orders"));
      toast.error(data.data.message, {
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
      });
    }
  };

  // Helper functions for complex logic
  const determineUserType = (managers, user) => {
    let userType = "strategist";

    if (managers && managers.length > 0) {
      managers.forEach((manager) => {
        if (manager.permissions.user.hasOwnProperty(user?.username)) {
          const userPermissionValue = manager.permissions.user[user?.username];
          if (userPermissionValue === 4 || userPermissionValue === 3) {
            userType = "admin";
          } else if (userPermissionValue === 2) {
            userType = "investor";
            dispatch(updateIsInvestor(true));
          }
        } else if (manager.owner === "user#" + user?.username) {
          userType = "admin";
        } else {
          userType = "strategist";
          dispatch(updateIsStrategist(true));
        }
      });
    } else {
      userType = "strategist";
      dispatch(updateIsStrategist(true));
    }

    return userType;
  };

  const handleAdminInvestorData = (data) => {
    const manager = data.managers;
    const findFund = manager[0]?.funds;
    const findSMA = manager[0]?.smas;

    const SMAArray = Object.values(findSMA).map((sma) => {
      const { smaId, account, ...rest } = sma;
      const accounts = {
        [account]: {
          number: account,
          custodian: "2400a58dc924508a59bf4ade3e4867f7",
        },
      };
      return { ...rest, fundId: smaId, accounts, isSMA: true };
    });

    let fundsArray = Object.values(findFund);
    const fundsToRemove = [
      "2c77f0f3c144ad70f13d461372f35c3f",
      "4fba8dab180873fa52820fd82629d7a9",
      "539d4f79097a6193c18cc90edb1bbe5f",
      "294bd402ffcb478ad9d72e21bf95a618",
      "8f52e97e7d26c01c3201d4d0253a7eaa",
      "143703e76a46abf29a80cc5a248806eb",
      "aa8e9a3418b3ae8504b037f4ba9cd69d",
    ];
    fundsArray = fundsArray.filter(fund => !fundsToRemove.includes(fund.fundId));
    fundsArray = SMAArray.concat(fundsArray);

    dispatch(setFunds(fundsArray)); // Use new funds slice

    const findAccounting = manager[0]?.accountingSystems;
    // Sort by number of funds (descending) to ensure the accounting system with most data is always first
    // This prevents inconsistent ordering from Object.values() which doesn't guarantee order
    const AccountingArray = Object.values(findAccounting).sort((a, b) => {
      const aCount = (a.funds?.length || 0) + (a.smas?.length || 0);
      const bCount = (b.funds?.length || 0) + (b.smas?.length || 0);
      return bCount - aCount;
    });
    const processedAccountingArray = AccountingArray.map((accountingItem) => {
      if (accountingItem.smas && Array.isArray(accountingItem.smas)) {
        const transformedSmas = accountingItem.smas
          .map((smaItem) => {
            // Create strategyAums object before transformation
            const strategyAums = {
              "d16c4685f2e706f9ea6be3dc771b02e6": smaItem?.aum,
            };

            return {
              ...smaItem,
              fund: smaItem.sma,
              isSMA: true,
              sma: undefined,
              strategyAUMs: strategyAums,
            };
          })
          .map(({ sma, ...rest }) => rest);
        const mergedFunds = [
          ...(accountingItem.funds || []),
          ...transformedSmas,
        ];

        return {
          ...accountingItem,
          funds: mergedFunds,
          smas: undefined,
        };
      }
      return accountingItem;
    });
    dispatch(setAccountingData(processedAccountingArray));
    dispatch(setStrategies(data.strategies));
    const reports = Object.values(Object.values(manager[0]?.reports));
    dispatch(setReports(reports));

    const combinedLots = fundsArray.reduce((acc, fund) => {
      if (fund.lots && Array.isArray(fund.lots)) {
        return acc.concat(fund.lots);
      }
      return acc;
    }, []);

    dispatch(setFundsWithLotsAndStrategies(processFundsWithLotsAndStrategies(combinedLots, fundsArray, data.strategies))); // Use new funds slice
    dispatch(setLots({ lots: combinedLots, trades: [] }));
  };

  const handleStrategistData = (data) => {
    dispatch(setStrategies(data.strategies));
    const manager = data.managers;
    const findFund = manager[0]?.funds;
    if (findFund) {
      const fundsArray = Object.values(findFund);
      const combinedLots = fundsArray.reduce((acc, fund) => {
        return acc.concat(Object.values(fund));
      }, []);
      dispatch(setLots({ lots: combinedLots, trades: [] }));
    }
  };

  // Refactored parseMsg function using Command Pattern
  const parseMsg = (data) => {
    // Handle server errors
    if (data?.message === "Internal server error") {
      //  toast.error(data.message);
      return;
    }

    if (!data) return;

    // Use switch statement for clean action routing
    const handler = messageHandlers[data.action];
    if (handler) {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error handling action ${data.action}:`, error);
        //  toast.error(`Failed to process ${data.action}`);
      }
    } else {
      console.warn(`No handler found for action: ${data.action}`);
    }
  };

  useEffect(() => {
    // Only clear loading state if we're not showing the skeleton
    if (req.length === 0 && !showInitialSkeleton) {
      dispatch(updateLoading(false));
      dispatch(updateLoadingMsg(""));
    }
  }, [req, showInitialSkeleton]);



  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);
  const [hasCompletedInitialSeed, setHasCompletedInitialSeed] = useState(false);
  const hasCompletedInitialSeedRef = useRef(false);

  // Keep the ref in sync with the state
  useEffect(() => {
    hasCompletedInitialSeedRef.current = hasCompletedInitialSeed;
  }, [hasCompletedInitialSeed]);

  // Reset connection attempt flag when user is removed (logout)
  useEffect(() => {
    if (!user) {
      setHasAttemptedConnection(false);
      setHasCompletedInitialSeed(false);
      resetSeedDataFlag(); // Disable reconnection attempts on logout
    }
  }, [user, resetSeedDataFlag]);

  // Auto-hide disconnect notification when connection is restored
  useEffect(() => {
    const handleConnect = () => {
      if (websocketDisconnected) {
        dispatch(setWebsocketDisconnected(false));
      }
      // Don't hide reconnect notification here - let seed processing handle it
      // if (websocketReconnectMode) {
      //   dispatch(setWebsocketReconnect(false));
      // }
    };

    const handleOnline = () => {
      // When internet comes back online, trigger reconnection if needed
      if (websocketDisconnected && !webSocketService.isReconnecting) {
        // The WebSocket service will handle reconnection automatically
        // We just need to reset the disconnected state when reconnection succeeds
      }
    };

    const handleOffline = () => {
      dispatch(setWebsocketDisconnected(true));
    };

    const handleReconnecting = (data) => {
      dispatch(setReconnecting(true));
    };

    const handleReconnectFailed = (data) => {
      if (navigator.onLine) {
        console.error("WebSocket reconnection failed:", data);
      }
      dispatch(setReconnecting(false));
      // Keep websocketReconnectMode as false to show manual reconnect option
      // dispatch(setWebsocketReconnect(true)); // Don't enable seed-fetching mode
    };



    const handleAuthError = async (data) => {
      try {
        // Try to refresh tokens when auth error occurs during reconnection
        const localUser = getAuthTokens();
        const refreshedUser = await tokenRefreshManager.refreshUserToken(localUser);
        if (refreshedUser && refreshedUser.accessToken !== user.accessToken) {
          // Update the stored user with new tokens
          dispatch(getUser(refreshedUser));

          // Update the WebSocket URL with the new token for future reconnection attempts
          if (webSocketService.lastConnectionUrl) {
            const url = new URL(webSocketService.lastConnectionUrl);
            url.searchParams.set('token', refreshedUser.accessToken);
            webSocketService.lastConnectionUrl = url.toString();
            console.log("[AUTH ERROR] Updated WebSocket URL with refreshed token for reconnection");
          }

          // The reconnection will continue with the next attempt using the new token
        }
      } catch (refreshError) {
        console.error("[AUTH ERROR] Token refresh failed during reconnection auth error:", refreshError);
      }
    };


    webSocketService.on('connect', handleConnect);
    webSocketService.on('reconnecting', handleReconnecting);
    webSocketService.on('reconnectFailed', handleReconnectFailed);
    webSocketService.on('authError', handleAuthError);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      webSocketService.removeListener('connect', handleConnect);
      webSocketService.removeListener('reconnecting', handleReconnecting);
      webSocketService.removeListener('reconnectFailed', handleReconnectFailed);
      webSocketService.removeListener('authError', handleAuthError);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [websocketDisconnected, dispatch, user]);

  useEffect(() => {
    // Only attempt connection once when user is first set, not on every re-render
    if (user && !webSocketService.isConnected && !hasAttemptedConnection) {
      setHasAttemptedConnection(true);
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Authenticating"));
      setShowInitialSkeleton(true);

      // Use JWT token from SRP authentication instead of session token
      const initiateWebSocketConnection = async () => {
        if (user.accessToken) {
          try {
            const currentUser = getAuthTokens();
            const url = `${process.env.REACT_APP_URL}?token=${currentUser.accessToken}&client=browser`;
            await connect(url);

            // Setup WebSocket event handlers
            webSocketService.on('error', (error) => {
              dispatch(updateLoading(false));
              dispatch(updateLoadingMsg(""));
              setShowInitialSkeleton(false);
              dispatch(setWebsocketDisconnected(true));
            });

            webSocketService.on('connect', () => {
              dispatch(setWebsocketDisconnected(false));
            });

            webSocketService.on('message', (data) => {
              if (data.action === "login") {
                // Check if seed is already in progress by looking at active requests
                const activeRequests = req; // from useAppState
                const hasActiveSeedRequest = activeRequests.includes("seed");
                const isReconnection = hasCompletedInitialSeedRef.current;

                if (!hasActiveSeedRequest) {
                  if (!isReconnection) {
                    // Initial login - show skeleton
                    setHasCompletedInitialSeed(true);
                    dispatch(updateLoading(true));
                    dispatch(updateLoadingMsg("Fetching Seed Data"));
                    setShowInitialSkeleton(true);
                  } else {
                    // Reconnection - show message in notification
                    dispatch(setWebsocketReconnect(true));
                  }

                  dispatch(getUserInfo(data?.data?.user));
                  dispatch(getApiInfo(data?.data));
                  dispatch(addActiveRequests("seed"));
                  send('{"action": "seed"}');
                } else {
                  // Seed already in progress, just update user info
                  dispatch(getUserInfo(data?.data?.user));
                  dispatch(getApiInfo(data?.data));
                }
              } else if (data.action === "download") {
                const handleDownload = async (file) => {
                  try {
                    const { readData } = Sockets(
                      apiDataRef.current,
                      user // Use the user object with JWT tokens instead of socketuser
                    );
                    dispatch(updateLoadingMsg("Downloading Data"));
                    const result = await readData(file);
                    const JSONmsg = JSON.parse(result);
                    console.log("s3 Data", JSONmsg);
                    parseMsg(JSONmsg);
                  } catch (error) {
                    console.error("Error during file download:", error);

                    // Provide more specific error messages based on the error type
                    let errorMessage = "Failed to download data from server.";
                    if (error.message) {
                      if (error.message.includes("session has expired")) {
                        errorMessage = "Your session has expired. Please log out and log back in.";
                      } else if (error.message.includes("S3 authentication failed")) {
                        errorMessage = "Authentication failed. Please try refreshing the page or logging out and back in.";
                      } else if (error.message.includes("S3 download failed")) {
                        errorMessage = error.message; // Use the specific error message
                      }
                    }

                    console.error(errorMessage);
                    dispatch(updateLoadingMsg(""));
                  }
                };
                handleDownload(data.file);
              } else {
                parseMsg(data);
              }
            });

            webSocketService.on('close', async (event) => {
              dispatch(setWebsocketDisconnected(true));

              // If connection closed due to authentication issues, try token refresh
              if (event.code === 4001 || event.code === 4003 || event.reason?.includes('token')) {
                try {
                  const localUser = getAuthTokens();
                  const refreshedUser = await tokenRefreshManager.refreshUserToken(localUser);
                  if (refreshedUser && refreshedUser.accessToken !== user.accessToken) {
                    // Try to reconnect with new token after a short delay
                    setTimeout(() => {
                      const newUrl = `${process.env.REACT_APP_URL}?token=${refreshedUser.accessToken}`;
                      connect(newUrl);
                    }, 1000);
                    return;
                  }
                } catch (refreshError) {
                  console.error("Token refresh failed for WebSocket reconnection:", refreshError);
                }
              }

              if (!user) {
                dispatch(removeUser());
                navigate("/login");
              }
            });
          } catch (error) {
            localStorage.removeItem("key");
            localStorage.removeItem("user");
            dispatch(removeUser());
            navigate("/login");
            dispatch(updateLoading(false));
            dispatch(updateLoadingMsg(""));
            setShowInitialSkeleton(false);
          }
        };
      }
      initiateWebSocketConnection();
    }
  }, [user, connect, send, dispatch, navigate, hasAttemptedConnection]);
  return (
    <ThemeProvider>
      <AppContent
        showInitialSkeleton={showInitialSkeleton}
        loadingmsg={loadingmsg}
        loading={loading}
        showHideButton={showHideButton}
        showMarketLoader={showMarketLoader}
        user={user}
        managers={managers}
        token={token}
        websocketDisconnected={websocketDisconnected}
        dispatch={dispatch}
        setShowInitialSkeleton={setShowInitialSkeleton}
        disconnect={disconnect}
        reconnect={reconnect}
        websocketReconnectMode={websocketReconnectMode}
      />
    </ThemeProvider>
  );
}

// Wrapper component to use theme context
function AppContent({
  showInitialSkeleton,
  loadingmsg,
  loading,
  showHideButton,
  showMarketLoader,
  user,
  managers,
  token,
  socketuser,
  websocketDisconnected,
  dispatch,
  setShowInitialSkeleton,
  disconnect,
  reconnect,
  websocketReconnectMode
}) {
  const { currentTheme } = useTheme();

  // Message snapshot state to preserve each loader's message independently
  const [loaderMessages, setLoaderMessages] = useState({});

  // Capture and preserve messages when loaders are first created
  useEffect(() => {
    if (loading && !loaderMessages["main-loading"]) {
      setLoaderMessages(prev => ({
        ...prev,
        "main-loading": loadingmsg || "Processing your request..."
      }));
    }
    if (showMarketLoader && !loaderMessages["market-data"]) {
      setLoaderMessages(prev => ({
        ...prev,
        "market-data": "Fetching Market Data"
      }));
    }
  }, [loading, showMarketLoader, loadingmsg, loaderMessages]);

  // Clear messages when loaders are removed
  useEffect(() => {
    if (!loading && loaderMessages["main-loading"]) {
      setLoaderMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages["main-loading"];
        return newMessages;
      });
    }
    if (!showMarketLoader && loaderMessages["market-data"]) {
      setLoaderMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages["market-data"];
        return newMessages;
      });
    }
  }, [loading, showMarketLoader, loaderMessages]);

  return (
    <MuiThemeProvider theme={currentTheme}>
      {showInitialSkeleton ? (
        <InitialLoadSkeleton message={loadingmsg || "Initializing..."} />
      ) : (
        <>
          {/* Multiple loaders with stacking */}
          {(loading || showMarketLoader) && (
            <StackedBottomRightLoaders
              loaders={[
                ...(loading ? [{
                  id: "main-loading",
                  message: loaderMessages["main-loading"] || "Processing your request...",
                  showHideButton: showHideButton,
                  showMarketLoader: false,
                  onHide: () => {
                    dispatch(updateLoading(false));
                    dispatch(removeActiveRequests("main-loading"));
                    dispatch(updateLoadingMsg(""));
                  }
                }] : []),
                ...(showMarketLoader ? [{
                  id: "market-data",
                  message: loaderMessages["market-data"] || "Fetching Market Data",
                  showHideButton: false,
                  showMarketLoader: true,
                  onHide: () => { }
                }] : [])
              ]}
            />
          )}

          {/* Websocket disconnection notification */}
          {(websocketDisconnected || websocketReconnectMode) && (
            <WebsocketDisconnectionNotification
              message="Attempting Reconnect"
              onReconnect={() => {
                const localUser = getAuthTokens();
                reconnect(localUser);
              }}
            />
          )}


          <ToastContainer />
          {managers ? (
            <MainRoutes
              token={token}
              managers={managers}
            />
          ) : null}
        </>
      )}
    </MuiThemeProvider>
  );
}

export default App;
